// @flow
import React, { Component } from 'react';
import cookie from 'js-cookie';
import URI from 'urijs';
import moment from 'moment';
import { appQueryParameters } from '@clubmed/utils';

import ReactAppContext from 'Context/ReactAppContext';
import { withRouter } from 'Client/router/withRouter';
import * as trackingApi from 'Tracking/tracking-api';
import { VIRTUAL_PAGE_LOAD } from 'Tracking/constants';
import redirectUtils from 'Utils/uri/redirectUtils';
import { convertLocationToURI } from 'Utils/uri/uriUtils';
import { getSpaUrl } from 'Components/frameworks/dynamic-link/DynamicLinkUtil';

import CookieWarningBanner from './CookieWarningBanner.jsx';
import { CookiePreferencesLayer } from './CookiePreferencesLayer.jsx';
import {
  COOKIES_ARRAY,
  COOKIE_PREFERENCES_STATS,
  COOKIE_PREFERENCES_PERSO,
  COOKIE_PREFERENCES_MEDIA,
  COOKIE_PREFERENCES_LAYER,
  COOKIE_WARNING_LOCALSTORAGE_KEY,
} from './constants';

export type CookieWarningProps = {
  approveAriaLabel: string,
  buttonLabel: string,
  closeLabel?: string,
  layerInfo: {
    back: string,
    backAriaLabel: string,
    link: string,
    item1: {
      title: string,
      label: string,
    },
    item2: {
      title: string,
      label: string,
    },
    item3: {
      title: string,
      label: string,
    },
    item4: {
      title: string,
      label: string,
    },
  },
  link: {
    label: string,
    url: string,
  },
  text: string,
  title: string,
};

type Props = CookieWarningProps & {
  isAppMounted: Boolean,
  isApproved: boolean,
  history: RouterHistory,
  isApproved: boolean,
  location: RouterLocation<{}, { openLayer: string }>,
};

type State = {
  hasApproved: boolean,
  personnalCookies: { [string]: boolean },
  showBanner: boolean,
  showLayer: boolean,
};

export class _CookieWarning extends Component<Props, State> {
  static contextType = ReactAppContext;
  static defaultProps = {
    buttonLabel: '',
    isApproved: false,
    layerInfo: {
      back: '',
      link: '',
      item1: {
        title: '',
        label: '',
      },
      item2: {
        title: '',
        label: '',
      },
      item3: {
        title: '',
        label: '',
      },
      item4: {
        title: '',
        label: '',
      },
    },
    link: {
      label: '',
      url: '',
    },
    text: '',
    title: '',
  };

  state = {
    hasApproved: false,
    personnalCookies: {
      [COOKIE_PREFERENCES_STATS]: true,
      [COOKIE_PREFERENCES_PERSO]: true,
      [COOKIE_PREFERENCES_MEDIA]: true,
    },
    showBanner: true,
    showLayer: false,
  };

  handler = null;

  static getDerivedStateFromProps(props: Props, state: State) {
    // first time rendering check if cookies were already approved
    // TODO change this because it prevents the customization after having set the cookies
    if (props.isApproved) {
      return { showBanner: false, showLayer: false, hasApproved: true };
    }

    // check in the location if we have to open the cookie preferences layer
    if (
      props.isAppMounted &&
      props.location.queryParams[appQueryParameters.openLayer] === COOKIE_PREFERENCES_LAYER &&
      !state.showLayer
    ) {
      return {
        showBanner: false,
        showLayer: true,
      };
    }

    if (
      (props.location.queryParams[appQueryParameters.openLayer] !== COOKIE_PREFERENCES_LAYER ||
        !props.location.queryParams[appQueryParameters.openLayer]) &&
      state.showLayer
    ) {
      return {
        showBanner: !state.hasApproved,
        showLayer: false,
      };
    }

    return null;
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.location.pathname !== this.props.location.pathname && !this.state.hasApproved) {
      this.setState({ showBanner: false, showLayer: false, hasApproved: true }, this.updateCookies);
    }
  }

  updateCookies() {
    const cookieOptions = {
      expires: moment()
        .add(13, 'month')
        .toDate(),
    };
    let cookieValue;

    cookie.set(COOKIE_WARNING_LOCALSTORAGE_KEY, 1, cookieOptions);

    COOKIES_ARRAY.forEach(cookieName => {
      cookieValue = this.state.personnalCookies[cookieName];
      cookie.set(cookieName, +cookieValue, cookieOptions);
    });

    // retrieve current virtualPageLoadingDataLayer
    const currentPageDataLayer =
      trackingApi.getDataLayer().google.find(layer => layer.event === VIRTUAL_PAGE_LOAD) || {};
    trackingApi.setDataLayer({ ...currentPageDataLayer, rgpd: 'OK' }, this.context);
  }

  acceptCookies = () => {
    this.updateCookies();
    this.setState({ showBanner: false, hasApproved: true });
  };

  onSwitchChange = (index: number) => (name: string, checked: boolean) => {
    const realIndex = index - 1;

    this.setState(prevState => ({
      ...prevState,
      personnalCookies: {
        // $FlowFixMe
        ...prevState.personnalCookies,
        // $FlowFixMe
        [COOKIES_ARRAY[realIndex]]: checked,
      },
    }));
  };

  onValidatePreferences = () => {
    this.updateCookies();
    this.setState({ hasApproved: true }, this.closeLayer);
  };

  redirect = (location: RouterLocation<{}, { openLayer: string }>, type: string) => {
    const fullURL = convertLocationToURI(location)
      [type]({ [appQueryParameters.openLayer]: COOKIE_PREFERENCES_LAYER })
      .toString();

    redirectUtils.redirectTo()(getSpaUrl(new URI(fullURL)), { shallow: true });
  };

  closeLayer = (): void => {
    this.redirect(this.props.location, 'removeQuery');
  };

  render() {
    const {
      approveAriaLabel,
      buttonLabel,
      closeLabel,
      layerInfo,
      link,
      location,
      text,
      title,
    } = this.props;
    const { back, backAriaLabel, link: openLayerLabel, ...rest } = layerInfo || {};

    if (this.state.showBanner && text) {
      return (
        <CookieWarningBanner
          acceptCookies={this.acceptCookies}
          approveAriaLabel={approveAriaLabel}
          buttonLabel={buttonLabel}
          cookieKey={COOKIE_WARNING_LOCALSTORAGE_KEY}
          dataSelector="cookieWarning"
          link={link}
          openLayer={() => this.redirect(location, 'addQuery')}
          openLayerLabel={openLayerLabel}
          text={text}
          title={title}
        />
      );
    }

    if (this.state.showLayer) {
      return (
        <CookiePreferencesLayer
          approveAriaLabel={approveAriaLabel}
          back={back}
          backAriaLabel={backAriaLabel}
          buttonLabel={buttonLabel}
          closeLabel={closeLabel}
          closeLayer={this.closeLayer}
          items={rest}
          link={link}
          onChange={this.onSwitchChange}
          onValidatePreferences={this.onValidatePreferences}
          personnalCookiesState={this.state.personnalCookies}
          text={text}
          title={title}
        />
      );
    }

    return null;
  }
}

export const CookieWarning = withRouter(_CookieWarning);
